/**
 * Library route — browse every question in an app's catalog.
 *
 * Currently surfaced only for einbuergerungstest (per locked decision L1:
 * generic Library UI deferred until two apps clearly need it). Lives under
 * /apps/:appId/questions so the URL shape is app-agnostic — the route
 * imports the einbuergerungstest payload shape for its current render path.
 *
 * Features in v1:
 *   - Status filter (All / Open / Learned), bookmark filter, topic filter.
 *   - Sort by BAMF ID (default, sectioned general → state) or by
 *     "times incorrect" descending to drill into weak spots.
 *   - Row click opens a read-only detail modal — keep the engine clean
 *     (no stealth practice from the list, per locked decision L3).
 *   - Bookmark toggle inline in each row with optimistic update.
 */
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bookmark, Check, Info, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { EinbuergerungsPayload, EinbuergerungsTopic } from '@/core/types';
import { api, type LibraryCard } from '@/lib/api';
import { useLibrary } from '@/lib/queries';
import { cn } from '@/lib/utils';

import { QuestionImage } from '@/apps/einbuergerungstest/QuestionImage';

type StatusFilter = 'all' | 'open' | 'learned';
type BookmarkFilter = 'all' | 'only';
type ScopeFilter = 'all' | 'general' | 'state';
type SortMode = 'bamf_id' | 'most_wrong';

const TOPIC_LABELS: Record<EinbuergerungsTopic, string> = {
  grundrechte: 'Rights',
  geschichte: 'History',
  geographie: 'Geography',
  demokratie: 'Democracy',
  staat: 'State',
  other: 'Other',
};

const TOPIC_ORDER: EinbuergerungsTopic[] = [
  'grundrechte',
  'geschichte',
  'geographie',
  'demokratie',
  'staat',
  'other',
];

export function LibraryRoute() {
  const { appId } = useParams<{ appId: string }>();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [bookmark, setBookmark] = useState<BookmarkFilter>('all');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [topic, setTopic] = useState<EinbuergerungsTopic | 'all'>('all');
  const [sortMode, setSortMode] = useState<SortMode>('bamf_id');
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [status, bookmark, scope, topic, sortMode]);

  const libraryQuery = useLibrary(appId, {
    status,
    bookmark,
    scope,
    ...(topic !== 'all' ? { topic } : {}),
  });

  // Memoised because `?? []` builds a fresh array on every render, which would
  // invalidate the sort below each time and re-sort the whole catalogue.
  const cards = useMemo(() => libraryQuery.data?.cards ?? [], [libraryQuery.data]);

  const sortedCards = useMemo(() => {
    if (sortMode === 'most_wrong') {
      return [...cards].sort((a, b) => {
        const aWrong = a.counts.practice_incorrect + a.counts.sim_incorrect;
        const bWrong = b.counts.practice_incorrect + b.counts.sim_incorrect;
        if (aWrong !== bWrong) return bWrong - aWrong;
        return compareBamf(a.external_id, b.external_id);
      });
    }
    // Default: general first (sectioned), then state, sorted by bamf_id.
    return [...cards];
  }, [cards, sortMode]);

  const PAGE_SIZE = 50;
  const visibleCards = sortedCards.slice(0, page * PAGE_SIZE);
  const hasMore = visibleCards.length < sortedCards.length;

  const selectedCard = sortedCards.find((c) => c.id === selectedCardId) ?? null;

  return (
    <div className="min-h-screen">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/apps/${appId}`}>
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
          <h1 className="font-display text-xl font-semibold">Browse questions</h1>
          <div className="text-muted-foreground ml-auto text-sm">
            {libraryQuery.isLoading ? '…' : `${cards.length} shown`}
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
        <Filters
          status={status}
          setStatus={setStatus}
          bookmark={bookmark}
          setBookmark={setBookmark}
          scope={scope}
          setScope={setScope}
          topic={topic}
          setTopic={setTopic}
          sortMode={sortMode}
          setSortMode={setSortMode}
        />

        {libraryQuery.isLoading ? (
          <p className="text-muted-foreground py-12 text-center text-sm">Loading questions…</p>
        ) : libraryQuery.error ? (
          <p className="text-destructive py-12 text-center text-sm">
            Couldn't load: {libraryQuery.error.message}
          </p>
        ) : sortedCards.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center text-sm">
            No questions match the current filters.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <ul className="flex flex-col gap-2">
              {visibleCards.map((card) => (
                <li key={card.id}>
                  <CardRow card={card} onSelect={() => setSelectedCardId(card.id)} />
                </li>
              ))}
            </ul>
            {hasMore ? (
              <div className="flex justify-center pt-2">
                <Button variant="outline" onClick={() => setPage((p) => p + 1)}>
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </main>

      <DetailDialog
        card={selectedCard}
        onOpenChange={(open) => setSelectedCardId(open ? selectedCardId : null)}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Filters
// -----------------------------------------------------------------------------

function Filters({
  status,
  setStatus,
  bookmark,
  setBookmark,
  scope,
  setScope,
  topic,
  setTopic,
  sortMode,
  setSortMode,
}: {
  status: StatusFilter;
  setStatus: (v: StatusFilter) => void;
  bookmark: BookmarkFilter;
  setBookmark: (v: BookmarkFilter) => void;
  scope: ScopeFilter;
  setScope: (v: ScopeFilter) => void;
  topic: EinbuergerungsTopic | 'all';
  setTopic: (v: EinbuergerungsTopic | 'all') => void;
  sortMode: SortMode;
  setSortMode: (v: SortMode) => void;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <ChipGroup
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all', label: 'All' },
            { value: 'open', label: 'Open' },
            { value: 'learned', label: 'Learned' },
          ]}
        />
        <ChipGroup
          label="Bookmark"
          value={bookmark}
          onChange={setBookmark}
          options={[
            { value: 'all', label: 'All' },
            { value: 'only', label: 'Only bookmarked' },
          ]}
        />
        <ChipGroup
          label="Scope"
          value={scope}
          onChange={setScope}
          options={[
            { value: 'all', label: 'All' },
            { value: 'general', label: 'General' },
            { value: 'state', label: 'My state' },
          ]}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <ChipGroup
          label="Topic"
          value={topic}
          onChange={setTopic}
          options={[
            { value: 'all', label: 'All' },
            ...TOPIC_ORDER.map((t) => ({ value: t, label: TOPIC_LABELS[t] })),
          ]}
        />
        <ChipGroup
          label="Sort"
          value={sortMode}
          onChange={setSortMode}
          options={[
            { value: 'bamf_id', label: 'BAMF #' },
            { value: 'most_wrong', label: 'Most wrong' },
          ]}
        />
      </div>
    </section>
  );
}

function ChipGroup<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground text-xs tracking-wider uppercase">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              value === opt.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Card row
// -----------------------------------------------------------------------------

function CardRow({ card, onSelect }: { card: LibraryCard; onSelect: () => void }) {
  const queryClient = useQueryClient();
  const payload = card.payload as unknown as EinbuergerungsPayload;
  const totalCorrect = card.counts.practice_correct + card.counts.sim_correct;
  const totalWrong = card.counts.practice_incorrect + card.counts.sim_incorrect;

  const toggleBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !card.bookmarked;
    try {
      await api.toggleBookmark(card.id, next);
      await queryClient.invalidateQueries({ queryKey: ['apps', card.id], exact: false });
      await queryClient.invalidateQueries({
        queryKey: ['apps', undefined, 'library'],
        exact: false,
        predicate: (q) => q.queryKey[0] === 'apps' && q.queryKey[2] === 'library',
      });
    } catch {
      // Surface no toast; the user will see the icon revert on next refetch.
    }
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className="border-border bg-card hover:border-primary/40 focus-visible:ring-ring flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
    >
      <div className="flex flex-col items-center gap-1 pt-1">
        <span className="text-muted-foreground text-xs font-semibold tabular-nums">
          {card.external_id ?? `#${card.id}`}
        </span>
        <StatusPip memorized={card.memorized} streak={card.streak} />
      </div>

      <div className="flex flex-1 flex-col gap-1.5">
        <p className="line-clamp-2 text-sm leading-snug">{card.de}</p>

        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="muted">
            {payload?.scope === 'state' && payload.state_code ? payload.state_code : 'General'}
          </Badge>
          {payload?.topic ? (
            <Badge variant="outline" className="text-[10px]">
              {TOPIC_LABELS[payload.topic] ?? payload.topic}
            </Badge>
          ) : null}
          <span className="text-muted-foreground text-xs">
            <span className="text-success">✓{totalCorrect}</span>
            {' · '}
            <span className="text-destructive">✗{totalWrong}</span>
          </span>
        </div>
      </div>

      <button
        type="button"
        aria-label={card.bookmarked ? 'Remove bookmark' : 'Add bookmark'}
        onClick={toggleBookmark}
        className={cn(
          'text-muted-foreground hover:bg-muted hover:text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
          card.bookmarked && 'text-primary',
        )}
      >
        <Bookmark className={cn('h-4 w-4', card.bookmarked && 'fill-current')} />
      </button>
    </button>
  );
}

function StatusPip({ memorized, streak }: { memorized: boolean; streak: number }) {
  if (memorized) {
    return (
      <span className="bg-success/20 text-success rounded-full px-1.5 py-0.5 text-[10px] font-medium">
        Learned
      </span>
    );
  }
  // Show streak progress: 0/5 to 4/5 (5+ flips to "Learned" above).
  return (
    <span className="bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums">
      {streak}/5
    </span>
  );
}

// -----------------------------------------------------------------------------
// Detail modal
// -----------------------------------------------------------------------------

function DetailDialog({
  card,
  onOpenChange,
}: {
  card: LibraryCard | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={!!card} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        {card ? <DetailContent card={card} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function DetailContent({ card }: { card: LibraryCard }) {
  const queryClient = useQueryClient();
  const payload = card.payload as unknown as EinbuergerungsPayload;
  const correctLetter = payload?.correct_letter;

  const toggleBookmark = async () => {
    const next = !card.bookmarked;
    try {
      await api.toggleBookmark(card.id, next);
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'apps' && q.queryKey[2] === 'library',
      });
    } catch {
      // ignore
    }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center justify-between gap-3">
          <DialogTitle className="text-left">
            Question {card.external_id ?? `#${card.id}`}
            {payload?.scope === 'state' && payload.state_code ? ` · ${payload.state_code}` : ''}
          </DialogTitle>
          <button
            type="button"
            aria-label={card.bookmarked ? 'Remove bookmark' : 'Add bookmark'}
            onClick={toggleBookmark}
            className={cn(
              'text-muted-foreground hover:bg-muted hover:text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
              card.bookmarked && 'text-primary',
            )}
          >
            <Bookmark className={cn('h-4 w-4', card.bookmarked && 'fill-current')} />
          </button>
        </div>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <div>
          <h3 className="font-display text-lg leading-snug font-semibold">{card.de}</h3>
          {payload?.question_en ? (
            <p className="text-muted-foreground mt-1 text-sm italic">{payload.question_en}</p>
          ) : null}
        </div>

        {payload?.has_image && card.external_id ? (
          <QuestionImage bamfId={card.external_id} size="md" />
        ) : null}

        {payload?.options ? (
          <ul className="flex flex-col gap-2">
            {payload.options.map((opt) => {
              const isCorrect = opt.letter === correctLetter;
              return (
                <li
                  key={opt.letter}
                  className={cn(
                    'flex items-start gap-3 rounded-md border p-3 text-sm',
                    isCorrect ? 'border-success bg-success/10' : 'border-border bg-card opacity-80',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-semibold',
                      isCorrect ? 'bg-success text-success-foreground' : 'bg-muted text-foreground',
                    )}
                  >
                    {opt.letter}
                  </span>
                  <span className="flex-1">
                    <span className="block">{opt.de}</span>
                    <span className="text-muted-foreground mt-0.5 block text-xs italic">
                      {opt.en}
                    </span>
                  </span>
                  {isCorrect ? <Check className="text-success size-4" /> : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {payload?.tip_en ? (
          <p className="border-primary/30 bg-primary/5 rounded-md border p-3 text-sm">
            <Info className="text-primary mr-1.5 inline size-3.5 -translate-y-px" />
            <span className="font-medium">Tip: </span>
            {payload.tip_en}
          </p>
        ) : null}

        <section className="border-border bg-muted/30 grid grid-cols-2 gap-3 rounded-lg border p-3 text-sm">
          <div>
            <p className="text-muted-foreground text-xs tracking-wider uppercase">Practice</p>
            <p className="mt-0.5">
              <span className="text-success">✓ {card.counts.practice_correct}</span>
              {' · '}
              <span className="text-destructive">✗ {card.counts.practice_incorrect}</span>
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs tracking-wider uppercase">Simulation</p>
            <p className="mt-0.5">
              <span className="text-success">✓ {card.counts.sim_correct}</span>
              {' · '}
              <span className="text-destructive">✗ {card.counts.sim_incorrect}</span>
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs tracking-wider uppercase">Status</p>
            <p className="mt-0.5">
              {card.memorized ? (
                <span className="text-success">Learned</span>
              ) : (
                <span>
                  Streak {card.streak}/5
                  {card.last_attempt_correct === false ? (
                    <X className="text-destructive ml-1 inline size-3.5" />
                  ) : null}
                </span>
              )}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs tracking-wider uppercase">Last attempt</p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {card.updated_at
                ? new Date(card.updated_at.replace(' ', 'T') + 'Z').toLocaleString()
                : '—'}
            </p>
          </div>
        </section>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Compare BAMF IDs lexicographically with numeric prefix ordering. */
function compareBamf(a: string | null, b: string | null): number {
  if (!a) return 1;
  if (!b) return -1;
  // Numeric IDs ("1" < "21") sort before state IDs ("BY-01").
  const aNum = /^\d+$/.test(a);
  const bNum = /^\d+$/.test(b);
  if (aNum && bNum) return Number(a) - Number(b);
  if (aNum) return -1;
  if (bNum) return 1;
  return a.localeCompare(b);
}
